import { Component } from '@theme/component';

/**
 * Snug Whiskey Fest — Venues Page Component
 *
 * Reads inline venue + event JSON, renders venue cards as a swipe deck.
 * Swipe/drag right = visit the venue page. Swipe left = skip.
 */
class SwfVenuesComponent extends Component {
  #venues = [];
  #events = [];
  #grouped = [];

  #cards = [];
  #history = [];
  #topIndex = 0;

  #drag = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadData();
    this.refs.reset?.addEventListener('click', () => this.#renderDeck());
  }

  // ── Data loading ──────────────────────────────────────────────

  #loadData() {
    try {
      const venueScript = this.querySelector('script[data-venues]');
      const eventScript = this.querySelector('script[data-events]');

      this.#venues = venueScript ? JSON.parse(venueScript.textContent) : [];
      this.#events = eventScript ? JSON.parse(eventScript.textContent) : [];

      this.refs.loading?.setAttribute('hidden', '');
      this.#groupVenues();
      this.#renderDeck();
    } catch (err) {
      console.error('[swf-venues] Failed to load data:', err);
      if (this.refs.loading) {
        this.refs.loading.innerHTML =
          '<span style="color:#b91c1c">Unable to load venue data. Please refresh the page.</span>';
      }
    }
  }

  // ── Venue grouping ────────────────────────────────────────────

  #groupVenues() {
    const map = new Map();
    for (const venue of this.#venues) {
      const lat = parseFloat(venue.map_lat);
      const lng = parseFloat(venue.map_lng);
      if (isNaN(lat) || isNaN(lng)) continue;

      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          lat,
          lng,
          venues: [],
          primaryName: venue.name,
          image: this.#getImageUrl(venue.featured_image),
        });
      }
      map.get(key).venues.push(venue);
    }
    this.#grouped = [...map.values()];
  }

  #getImageUrl(img) {
    if (!img) return null;
    if (typeof img === 'string') return img;
    return img.url || null;
  }

  /** Aligns Liquid swf-venue-display-name: strip trailing " (…)" for matching. */
  #venueLabelKey(s) {
    const t = (s || '').trim().toLowerCase();
    const i = t.indexOf(' (');
    return i >= 0 ? t.slice(0, i) : t;
  }

  #getEventsForGroup(group) {
    const keys = new Set(group.venues.map((v) => this.#venueLabelKey(v.name)));
    return this.#events.filter((evt) => keys.has(this.#venueLabelKey(evt.venueName)));
  }

  // ── Deck rendering ────────────────────────────────────────────

  #isGridMode() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #renderDeck() {
    const deck = this.refs.deck;
    if (!deck) return;

    const gridMode = this.#isGridMode();
    deck.classList.toggle('swf-venues__deck--grid', gridMode);

    deck.innerHTML = '';
    this.#cards = [];
    this.#history = [];
    this.#topIndex = 0;

    this.#grouped.forEach((group, i) => {
      const card = this.#buildCard(group, i, gridMode);
      deck.appendChild(card);
      this.#cards.push(card);
    });

    if (gridMode) {
      this.#toggleEmpty(false);
    } else {
      this.#applyStack();
    }
  }

  #buildCard(group, index, gridMode = false) {
    const events = this.#getEventsForGroup(group);
    const primaryVenue = group.venues[0];

    const imageUrl = group.image;
    const heroHtml = imageUrl
      ? `<img src="${this.#esc(imageUrl)}" alt="${this.#esc(group.primaryName)}" loading="lazy" draggable="false">`
      : `<div class="swf-venues__card-hero-placeholder"><span class="swf-venues__card-hero-initial">${(group.primaryName || '?').charAt(0).toUpperCase()}</span></div>`;

    const subVenuesHtml = group.venues.length > 1
      ? `<p class="swf-venues__card-subvenues">${group.venues.map((v) => this.#esc(v.name)).join(' &bull; ')}</p>`
      : '';

    const address = primaryVenue.address || '';
    const city = primaryVenue.city || '';
    const addressStr = [address, city].filter(Boolean).join(', ');

    const desc = this.#getPlainDescription(primaryVenue.description);
    const descHtml = desc
      ? `<p class="swf-venues__card-desc">${this.#esc(desc)}</p>`
      : '';

    const venueHandle = primaryVenue.handle || '';
    const pageUrl = venueHandle ? `/pages/visit-${this.#esc(venueHandle)}` : '';

    const eventsLabel = events.length > 0
      ? `<strong>${events.length}</strong> event${events.length !== 1 ? 's' : ''}`
      : 'Event info coming soon…';

    const card = document.createElement('a');
    card.className = 'swf-venues__card';
    card.href = pageUrl || '#';
    card.setAttribute('aria-label', group.primaryName);
    card.dataset.index = String(index);
    card.innerHTML = `
      <span class="swf-venues__stamp swf-venues__stamp--like">Next</span>
      <span class="swf-venues__stamp swf-venues__stamp--nope">Next</span>
      <div class="swf-venues__card-hero">
        ${heroHtml}
        ${events.length > 0 ? `<span class="swf-venues__card-badge">${events.length} event${events.length !== 1 ? 's' : ''}</span>` : ''}
      </div>
      <div class="swf-venues__card-body">
        <h2 class="swf-venues__card-name">${this.#esc(group.primaryName)}</h2>
        ${primaryVenue.handle === 'snug' ? '<p class="swf-venues__card-hq">Festival HQ</p>' : ''}
        ${subVenuesHtml}
        ${addressStr ? `
          <p class="swf-venues__card-address">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            ${this.#esc(addressStr)}
          </p>` : ''}
        ${descHtml}
      </div>
      <div class="swf-venues__card-footer">
        <span class="swf-venues__card-events-label">${eventsLabel}</span>
        <span class="swf-venues__card-link">View venue &rarr;</span>
      </div>`;

    if (!gridMode) this.#attachDrag(card);
    return card;
  }

  // ── Stack layout ──────────────────────────────────────────────

  #applyStack() {
    const total = this.#cards.length;
    for (let i = 0; i < total; i++) {
      const card = this.#cards[i];
      if (card.classList.contains('is-gone')) continue;

      const depth = i - this.#topIndex;
      if (depth < 0) {
        card.style.transform = '';
        card.style.opacity = '0';
        card.style.pointerEvents = 'none';
        card.style.zIndex = '0';
        continue;
      }

      // Show only top 3 for depth; deeper cards hide
      const maxVisible = 3;
      if (depth >= maxVisible) {
        card.style.transform = `translateY(${(maxVisible - 1) * 10}px) scale(${1 - (maxVisible - 1) * 0.04})`;
        card.style.opacity = '0';
        card.style.pointerEvents = 'none';
        card.style.zIndex = String(total - i);
        continue;
      }

      const translate = depth * 10;
      const scale = 1 - depth * 0.04;
      card.style.transform = `translateY(${translate}px) scale(${scale})`;
      card.style.opacity = '1';
      card.style.zIndex = String(total - depth);
      card.style.pointerEvents = depth === 0 ? 'auto' : 'none';
    }

    this.#toggleEmpty(this.#topIndex >= total);
  }

  #toggleEmpty(isEmpty) {
    const empty = this.refs.empty;
    if (empty) {
      if (isEmpty) empty.removeAttribute('hidden');
      else empty.setAttribute('hidden', '');
    }
  }

  #topCard() {
    return this.#cards[this.#topIndex] || null;
  }

  #swipeTop(direction) {
    const card = this.#topCard();
    if (!card) return;
    this.#flingOff(card, direction);
  }

  #flingOff(card, direction) {
    const width = card.offsetWidth || 400;
    const travelX = direction * (width * 2);
    const rot = direction * 25;

    card.classList.add('is-gone');
    card.style.transition = 'transform 0.55s cubic-bezier(0.2, 0.7, 0.3, 1), opacity 0.5s ease';
    card.style.transform = `translate(${travelX}px, -40px) rotate(${rot}deg)`;
    card.style.opacity = '0';

    this.#history.push({ index: this.#topIndex, direction });
    this.#topIndex += 1;

    // Shift the rest of the stack up with a short delay so it feels tactile
    window.requestAnimationFrame(() => this.#applyStack());

    // Recycle: after the card flies off, move it to the bottom of the deck.
    window.setTimeout(() => {
      this.#recycleCard(card);
    }, 600);
  }

  #recycleCard(card) {
    const idx = this.#cards.indexOf(card);
    if (idx < 0) return;
    this.#cards.splice(idx, 1);
    this.#cards.push(card);
    if (idx < this.#topIndex) this.#topIndex -= 1;

    // Reset the card so it can ride the stack again as the bottom slide.
    card.classList.remove('is-gone');
    card.style.transition = 'none';
    card.style.transform = '';
    card.style.opacity = '0';
    this.#updateStamps(card, 0);
    // Reorder in DOM so z-index/stacking remains consistent.
    card.parentNode?.appendChild(card);

    void card.offsetWidth;
    card.style.transition = '';
    window.requestAnimationFrame(() => this.#applyStack());
  }

  #rewind() {
    const last = this.#history.pop();
    if (!last) return;
    const card = this.#cards[last.index];
    if (!card) return;

    this.#topIndex = last.index;
    card.classList.remove('is-gone');
    // Snap to off-screen start position instantly, then animate into stack
    card.style.transition = 'none';
    card.style.opacity = '0';
    card.style.transform = `translate(${last.direction * 260}px, -20px) rotate(${last.direction * 15}deg)`;
    // Force reflow so the starting state is committed before re-enabling transitions
    void card.offsetWidth;
    card.style.transition = '';
    this.#updateStamps(card, 0);

    window.requestAnimationFrame(() => this.#applyStack());
  }

  // ── Drag handling ─────────────────────────────────────────────

  #attachDrag(card) {
    card.addEventListener('pointerdown', (e) => this.#onPointerDown(e, card));
    card.addEventListener('dragstart', (e) => e.preventDefault());
    card.addEventListener('click', (e) => {
      // Block the anchor click if the user just dragged this card
      if (card.dataset.suppressClick === '1') {
        e.preventDefault();
        e.stopPropagation();
        card.dataset.suppressClick = '0';
      }
    });
  }

  #onPointerDown(e, card) {
    // Only for the top card
    if (card !== this.#topCard()) return;
    if (e.button !== undefined && e.button !== 0) return;

    card.setPointerCapture(e.pointerId);
    this.#drag = {
      card,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      dy: 0,
      moved: false,
    };
    card.classList.add('is-dragging');

    const onMove = (ev) => this.#onPointerMove(ev);
    const onUp = (ev) => {
      this.#onPointerUp(ev);
      card.removeEventListener('pointermove', onMove);
      card.removeEventListener('pointerup', onUp);
      card.removeEventListener('pointercancel', onUp);
    };
    card.addEventListener('pointermove', onMove);
    card.addEventListener('pointerup', onUp);
    card.addEventListener('pointercancel', onUp);
  }

  #onPointerMove(e) {
    if (!this.#drag || e.pointerId !== this.#drag.pointerId) return;
    const dx = e.clientX - this.#drag.startX;
    const dy = e.clientY - this.#drag.startY;
    this.#drag.dx = dx;
    this.#drag.dy = dy;
    if (Math.abs(dx) + Math.abs(dy) > 6) this.#drag.moved = true;

    const card = this.#drag.card;
    const rot = dx * 0.06; // gentle rotation
    card.style.transform = `translate(${dx}px, ${dy}px) rotate(${rot}deg)`;
    this.#updateStamps(card, dx);
  }

  #onPointerUp(e) {
    if (!this.#drag || e.pointerId !== this.#drag.pointerId) return;
    const card = this.#drag.card;
    const dx = this.#drag.dx;
    const width = card.offsetWidth || 400;
    const threshold = width * 0.28;

    card.classList.remove('is-dragging');
    if (this.#drag.moved) card.dataset.suppressClick = '1';

    if (Math.abs(dx) > threshold) {
      this.#flingOff(card, dx > 0 ? 1 : -1);
    } else {
      // Snap back
      card.style.transform = '';
      this.#updateStamps(card, 0);
      window.requestAnimationFrame(() => this.#applyStack());
    }

    this.#drag = null;
  }

  #updateStamps(card, dx) {
    const like = card.querySelector('.swf-venues__stamp--like');
    const nope = card.querySelector('.swf-venues__stamp--nope');
    const width = card.offsetWidth || 400;
    const t = Math.max(-1, Math.min(1, dx / (width * 0.4)));
    if (like) like.style.opacity = t > 0 ? String(t) : '0';
    if (nope) nope.style.opacity = t < 0 ? String(-t) : '0';
  }

  // ── Helpers ───────────────────────────────────────────────────

  #getPlainDescription(desc) {
    if (!desc) return '';
    let text = '';
    if (typeof desc === 'string') {
      text = desc;
    } else if (typeof desc === 'object' && desc.children) {
      text = desc.children
        .map((block) =>
          (block.children || [])
            .map((child) => child.value || '')
            .join('')
        )
        .filter(Boolean)
        .join(' ');
    }
    if (text) {
      const div = document.createElement('div');
      div.innerHTML = text;
      return div.textContent || '';
    }
    return '';
  }

  #esc(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }
}

if (!customElements.get('swf-venues-component')) {
  customElements.define('swf-venues-component', SwfVenuesComponent);
}
